import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CelebrationProvider } from './contexts/CelebrationContext';
import { AppConfigProvider } from './contexts/AppConfigContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { VideoGenerationProvider } from './contexts/VideoGenerationContext';
import { supabase } from './lib/supabase';
import { Toaster } from 'react-hot-toast';
import InstallPrompt from './components/InstallPrompt';
import NotificationPermissionPrompt from './components/NotificationPermissionPrompt';
import { Analytics } from '@vercel/analytics/react';

import './utils/security'; // Import to run security cleanup

// Lazy-loaded route components
const LandingPage = lazy(() => import('./components/LandingPage'));

const Dashboard = lazy(() => import('./components/Dashboard'));
const KidsDashboardWrapper = lazy(() => import('./components/KidsDashboardWrapper'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const DataManagement = lazy(() => import('./components/DataManagement'));
const MyDayStandalonePage = lazy(() => import('./components/MyDayStandalonePage'));

// Minimal loading fallback
const RouteLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-jtc-violet" />
      <span className="text-sm text-gray-400 font-medium">Loading...</span>
    </div>
  </div>
);

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { user, hasProfile } = useAuth();
  if (!user) {
    // Preserve the intended destination so we can redirect after login
    const returnTo = window.location.pathname + window.location.search;
    const loginUrl = returnTo && returnTo !== '/' ? `/?returnTo=${encodeURIComponent(returnTo)}` : '/';
    return <Navigate to={loginUrl} />;
  }
  // Accept either Supabase field or normalized alias from secure storage
  const isEmailConfirmed = !!(user.email_confirmed_at || user.emailVerified);
  if (!isEmailConfirmed) {
    return <Navigate to="/" />;
  }
  // User verified OTP but never set password (no profile row created)
  if (hasProfile === false) {
    return <Navigate to="/" />;
  }
  return children;
};

// Auth callback handler
const AuthCallback = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Get the parameters from the URL
        const params = new URLSearchParams(window.location.search);
        const token = params.get('access_token');
        const type = params.get('type');


        // Handle recovery (password reset) flow
        if (type === 'recovery' && token) {
          try {
            // First, get the session to check if we have the email
            const { data: { session } } = await supabase.auth.getSession();

            if (session?.user?.email) {
              localStorage.setItem('resetEmail', session.user.email);
            }

            // Store the recovery token
            localStorage.setItem('resetToken', token);
            // Navigate to reset password page
            navigate('/reset-password', { replace: true });
            return;
          } catch (error) {
            console.error('Recovery flow error:', error);
            throw error;
          }
        }

        // For other auth flows, check session
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          navigate('/dashboard', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/forgot-password', {
          state: {
            message: 'Your password reset link has expired or is invalid. Please request a new one.'
          },
          replace: true
        });
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-landing-light via-landing to-landing-dark flex items-center justify-center">
      <div className="bg-white p-8 rounded-lg shadow-xl">
        <h2 className="text-2xl font-kalam text-center mb-4">Verifying...</h2>
        <div className="relative w-64 h-32 mx-auto">
          <div className="spaceship-loader">
            <span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </span>
            <div className="base">
              <span></span>
              <div className="face"></div>
            </div>
          </div>
          <div className="longfazers">
            <span></span>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Route wrapper that redirects to dashboard if authenticated
const RootRoute = () => {
  const { user, hasProfile } = useAuth();
  if (user && hasProfile === false) {
    // User verified OTP but never finished signup — send to complete it
    return <Navigate to="/" replace />;
  }
  if (user && hasProfile) {
    // Preserve returnTo param (set by ProtectedRoute when redirecting unauthenticated users)
    const returnTo = new URLSearchParams(window.location.search).get('returnTo');
    const dest = returnTo && returnTo.startsWith('/') ? returnTo : '/dashboard';
    return <Navigate to={dest} replace />;
  }
  return <LandingPage />;
};

// Route wrapper that redirects to dashboard if authenticated (for login page)
// const LoginRoute = () => { ... } // Removed

const ThemedToaster = () => {
  const { isDark } = require('./contexts/ThemeContext').useTheme();
  return (
    <Toaster
      position="top-right"
      gutter={6}
      toastOptions={{
        duration: 1800,
        style: {
          background: isDark ? 'rgba(30, 30, 30, 0.95)' : 'rgba(255, 255, 255, 0.95)',
          color: isDark ? '#e5e7eb' : '#1f2937',
          backdropFilter: 'blur(10px)',
          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '16px',
          boxShadow: isDark ? '0 8px 32px rgba(0, 0, 0, 0.4)' : '0 8px 32px rgba(0, 0, 0, 0.1)',
          padding: '12px 16px',
          fontSize: '13px',
          fontWeight: '500',
        },
        success: {
          iconTheme: { primary: '#10b981', secondary: isDark ? '#1e1e1e' : '#ffffff' },
        },
        error: {
          duration: 2500,
          iconTheme: { primary: '#ef4444', secondary: isDark ? '#1e1e1e' : '#ffffff' },
        },
      }}
    />
  );
};

function App() {
  return (
    <ThemeProvider>
      <CelebrationProvider>
        <AppConfigProvider>
          <VideoGenerationProvider>
            <AuthProvider>
              <Router>
                <div className="App">
                  <Suspense fallback={<RouteLoader />}>
                  <Routes>
                    <Route path="/" element={<RootRoute />} />
                    <Route path="/login" element={<RootRoute />} />
                    <Route path="/signup" element={<Navigate to="/" replace />} />
                    <Route path="/forgot-password" element={<Navigate to="/" replace />} />
                    <Route path="/reset-password" element={<Navigate to="/" replace />} />
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route
                      path="/data-management"
                      element={
                        <ProtectedRoute>
                          <DataManagement />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/dashboard/*"
                      element={
                        <ProtectedRoute>
                          <Dashboard />
                        </ProtectedRoute>
                      }
                    />
                    <Route path="/kids-dashboard" element={<KidsDashboardWrapper />} />
                    {/* Standalone, installable MyDay scope. Public at the route
                        level so the user can install the PWA before they ever
                        sign in. The component itself shows an inline auth gate
                        when not authenticated, so install-then-sign-up is a
                        first-class flow. */}
                    <Route path="/myday" element={<MyDayStandalonePage />} />
                  </Routes>
                  </Suspense>
                  {/* PWA Install Prompt */}
                  <InstallPrompt />
                  {/* Notification Permission Prompt */}
                  <NotificationPermissionPrompt />
                </div>
                <ThemedToaster />
              </Router>
              <Analytics />
            </AuthProvider>
          </VideoGenerationProvider>
        </AppConfigProvider>
      </CelebrationProvider>
    </ThemeProvider>
  );
}

export default App; 